// Packages
import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from "react-router-dom";

// Helpers
import useHelpers from '../../../../helpers';

// Controllers
import useControllers from '../../../'

// Models
import useModels from '../../../../models';

// Actions
import useApi from "../../../../api";

const useUsersEdit = () => {
  const [showModal, setShowModal] = useState(false)
  const [listRoles, setListRoles] = useState([])
  const [listCountries, setListCountries] = useState([]);
  const [activateUser, setActivateUser] = useState({label: "Inactivo", value: false})
  const [groupedPermissions, setGroupedPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleId, setRoleId] = useState(null);
  const [roleDefaultPermissions, setRoleDefaultPermissions] = useState([]);
  const roleProcessedRef = useRef(false); // Track if we've processed this role

  const navigation = useNavigate();
  const { id } = useParams(); // Get user ID from URL

  // Controllers
  const {useGeneralHooks} = useControllers()
  const {formatDate, filterCountriesByUserAccess} = useGeneralHooks()

  // Helpers
  const { useQuickFunctions } = useHelpers();
  const { useValidators } = useQuickFunctions();
  const { editUserValidationShema } = useValidators();

  // Models
  const { useSelectors } = useModels();
  const { useSelector, useAuthSelectors } = useSelectors();
  const { session } = useAuthSelectors();

  const { rol: currentUserRole } = useSelector(session);

  // Actions
  const { useActions } = useApi();
  const { useUsersActions, useGeneralActions, useRolesActions } = useActions();
  const { actEditUsers, actListRoles, actGetUserById } = useUsersActions();
  const { actListCountries, actListPermissions } = useGeneralActions();
  const { actGetRoleById } = useRolesActions();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(editUserValidationShema),
    defaultValues: {
      country_id: [],
      name: '',
      last_name: '',
      email: '',
      birth_date: '',
      abi_team: '',
      rol: '',
      permissions: [],
    }
  });

  const handleCloseModal = () => {
    setShowModal(false)
    navigation(-1)
  }

  const handleChangeActivate = () => {
    setActivateUser({label: !activateUser.value ? "Activo" : "Inactivo", value: !activateUser.value})
  }

  const handleShowModal = () => {
    setShowModal(true)
  }

  const loadDatarol = data => {
    setListRoles(data.map(v => {return {label: v.name, value: v.id}}))
  }
  const prepareDataCountry = (data) => {
    // Show all countries - not filtered by user's assigned countries
    // Only Súper Administrador and Administrador can edit this field (disabled in view)
    setListCountries(data.map(v => ({label: v.name, value: v.id})));
  };

  const loadPermissions = (data) => {
    // Group permissions the same way as roles
    const permissionsWithChildren = data.filter(perm =>
      data.some(other => other.key.startsWith(perm.key + '.'))
    );

    const parentKeys = new Set(permissionsWithChildren.map(p => p.key));
    const childKeys = new Set();
    data.forEach(perm => {
      const parts = perm.key.split('.');
      if (parts.length > 1) {
        childKeys.add(perm.key);
      }
    });

    const grouped = [];

    permissionsWithChildren.forEach(parent => {
      const children = data.filter(perm => {
        const parts = perm.key.split('.');
        const parentParts = parent.key.split('.');
        return perm.key.startsWith(parent.key + '.') && parts.length === parentParts.length + 1;
      });

      if (children.length > 0) {
        grouped.push({
          parent: { label: parent.name, value: parent.id, key: parent.key },
          children: children.map(child => ({
            label: child.name,
            value: child.id,
            key: child.key
          }))
        });
      }
    });

    const standalonePermissions = data.filter(perm =>
      !parentKeys.has(perm.key) && !childKeys.has(perm.key)
    );

    standalonePermissions.forEach(standalone => {
      grouped.push({
        parent: { label: standalone.name, value: standalone.id, key: standalone.key },
        children: []
      });
    });

    setGroupedPermissions(grouped);
  };


  const onSubmit = (data) => {
    // Format permissions correctly for API
    const formattedData = {
      ...data,
      country_id: data.country_id.map(c => c.value),
      active: activateUser.value,
      birth_date: formatDate(data.birth_date, "Y-mm-dd", false),
      permissions: data.permissions || [] // Already in correct format: [{id, type}]
    };
    actEditUsers(id, formattedData, handleShowModal);
  };

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = () => {
      actGetUserById(
        id,
        (data) => {
          setUserData(data);
          setLoading(false);
          roleProcessedRef.current = false; // Reset flag when new user loads
        },
        () => {
          setLoading(false);
          navigation(-1); // Go back if user not found
        }
      );
    };

    if (id) {
      fetchUserData();
    }
  }, [id]);

  // Update form when userData is loaded
  useEffect(() => {
    if (!userData || listCountries.length === 0) return;

    setActivateUser({label: userData.active ? "Activo" : "Inactivo", value: userData.active})

    // Extract role permissions for display
    if (userData.rol && userData.rol[0] && userData.rol[0].permissions) {
      const currentRoleId = userData.rol[0].id;
      const userPermissions = userData.rol[0].permissions;

      setRoleId(currentRoleId);
      setRolePermissions(userPermissions);

      // Only fetch role defaults once per userData load
      if (!roleProcessedRef.current) {
        roleProcessedRef.current = true;

        // Fetch role default permissions to detect overrides
        actGetRoleById(currentRoleId, (roleData) => {
          const roleDefaults = roleData.permissions || [];
          setRoleDefaultPermissions(roleDefaults);

          // Extract only user-specific overrides
          // Override = permission with non-null type (null means inherited from role)
          const overrides = userPermissions
            .filter(p => p.type !== null) // Only non-null types are overrides
            .map(p => ({ id: p.id, type: p.type }));

          // Transform country_id array to multi-select format
          // Note: country_id from API is array of strings: ["48", "62"]
          const countryOptions = userData.country_id?.map(countryId => {
            return listCountries.find(c => c.value === Number(countryId));
          }).filter(Boolean) || [];

          // Reset form with detected overrides
          reset({
            country_id: countryOptions,
            name: userData.name,
            last_name: userData.last_name,
            email: userData.email,
            birth_date: userData.birth_date,
            abi_team: userData.abi_team,
            rol: currentRoleId,
            permissions: overrides,
          });
        });
      }
    }
  }, [userData, listCountries])

  useEffect(() => {
    actListRoles(loadDatarol)
    actListCountries(prepareDataCountry);
    actListPermissions(loadPermissions);
  }, [])

  return {
    control,
    handleSubmit,
    onSubmit,
    showModal,
    handleCloseModal,
    userData,
    errors,
    activateUser,
    handleChangeActivate,
    listRoles,
    listCountries,
    groupedPermissions,
    rolePermissions,
    currentUserRole,
    loading,
    roleDefaultPermissions,
  };
};

export default useUsersEdit;
